import { useEffect, useRef } from 'react';

import { DataModelAttribute } from '@/types/DataModel';

import { isHiddenAttribute } from './useIsHiddenAttribute';

interface UseFocusOnNewRowParams {
    fields: Array<{ id: string }>;
    fieldPath: string;
    memberAttributes: DataModelAttribute[] | null | undefined;
    readonly?: boolean;
}

/**
 * Hook to automatically focus on the first column of a newly added row in collection-like components.
 * Works with Collection, MultiDimensional, and MultiReference components.
 *
 * @param fields - The fields array from useFieldArray
 * @param fieldPath - The form field path (e.g., "attributeName")
 * @param memberAttributes - Array of member attributes for the collection
 * @param readonly - Whether the component is readonly
 */
export const useFocusOnNewRow = ({
    fields,
    fieldPath,
    memberAttributes,
    readonly = false,
}: UseFocusOnNewRowParams) => {
    const previousFieldsLengthRef = useRef(fields.length);

    useEffect(() => {
        // Check if a new row was added
        if (fields.length > previousFieldsLengthRef.current && !readonly && memberAttributes) {
            const newRowIndex = fields.length - 1;

            // Get the first visible member attribute (excluding hidden attributes)
            const firstVisibleAttribute = memberAttributes.find((attr) => !isHiddenAttribute({ attribute: attr }));

            if (firstVisibleAttribute) {
                // Wait for DOM to update, then focus on the input
                setTimeout(() => {
                    const fieldPathForInput = `${fieldPath}.${newRowIndex}.${firstVisibleAttribute.name}`;
                    const inputElement = document.getElementById(fieldPathForInput) as HTMLInputElement;

                    if (inputElement) {
                        inputElement.focus();
                        // Move cursor to end of input if it has value
                        if (inputElement.setSelectionRange) {
                            const length = inputElement.value?.length || 0;
                            inputElement.setSelectionRange(length, length);
                        }
                    }
                }, 100);
            }
        }

        previousFieldsLengthRef.current = fields.length;
    }, [fields.length, fieldPath, readonly, memberAttributes]);
};
